import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { PolicyFlyApplicationRow } from "../../../client";
import { useClientId } from "../../../ClientConfig";
import { PantheonAvatarWithoutMenu } from "../../../components/avatar/Avatar";
import { CRMContact } from "../../../components/crm-contact/CRMContact";
import { EffectiveDateFilter } from "../../../components/filters/EffectiveDateFilter";
import {
  AssigneeFilterDropdown,
  FilterDropdown,
} from "../../../components/filters/Filters";
import { GoogleGroupIcon } from "../../../components/google-group/GoogleGroupIcon";
import { StateCards } from "../../../components/states/StateCards";
import { ROUTES } from "../../../routes";
import { displayNameForUser } from "../../../util";
import { formatAbbreviatedMoney } from "../Money";
import { useLocalUser, usePolicyFlyApplications, useUsers } from "../data";
import { readableLayer } from "../layers/util";
import styles from "./Dashboard.module.css";
import { featureEnabled } from "../../../client-features";
import { mmddyyyyToDate } from "../../../date-utils";
import {
  borderColorForAttributes,
  columnPtOptions,
  premiumSeverity,
} from "./util";
import { ThemeContext } from "../../../themes/Theme";

// Statuses shown on this dashboard. CANCELLED is intentionally excluded —
// cancelled applications shouldn't surface in the quoted/bound/in-force view.
const ALL_PF_STATUSES = "QUOTED,SUBMITTED,IN_FORCE";

const PF_STATUS_OPTIONS = [
  { name: "Quoted", code: "QUOTED" },
  { name: "Bound", code: "SUBMITTED" },
  { name: "In Force", code: "IN_FORCE" },
];

type SelectedUser = { name: string; id: string };

const readablePolicyFlyStatus = (status: string): string => {
  switch (status) {
    case "QUOTED":
      return "Quoted";
    case "SUBMITTED":
      return "Bound";
    case "IN_FORCE":
      return "In Force";
    case "CANCELLED":
      return "Cancelled";
    default:
      return status;
  }
};

const matchesSearch = (
  row: PolicyFlyApplicationRow,
  searchText: string
): boolean => {
  const lower = searchText.toLowerCase();
  const companyName = (
    row.report_metadata.company_info.private_computed_values.company_name ?? ""
  ).toLowerCase();
  const broker = (row.broker_contact?.organization_name ?? "").toLowerCase();
  const brokerAgent = (row.broker_contact?.full_name || "").toLowerCase();
  return (
    companyName.includes(lower) ||
    broker.includes(lower) ||
    brokerAgent.includes(lower)
  );
};

export const PolicyFlyDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = useClientId();
  const users = useUsers();
  const localUser = useLocalUser();
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";

  const { data, isLoading } = usePolicyFlyApplications(ALL_PF_STATUSES);
  const [searchText, setSearchText] = useState("");

  // Derive the status filter from the ?status= URL param (comma-separated to
  // support multi-select) so sidebar links stay the single source of truth.
  // Updating the filter in the dropdown writes back to the URL.
  const statusFromUrl = searchParams.get("status");
  const selectedStatuses = useMemo(() => {
    if (!statusFromUrl) return [];
    const codes = new Set(statusFromUrl.split(","));
    return PF_STATUS_OPTIONS.filter((s) => codes.has(s.code));
  }, [statusFromUrl]);
  const setSelectedStatuses = (next: { name: string; code: string }[]) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        if (next.length > 0) {
          newParams.set("status", next.map((s) => s.code).join(","));
        } else {
          newParams.delete("status");
        }
        return newParams;
      },
      { replace: true }
    );
  };

  const [selectedAssignees, setSelectedAssignees] = useState<SelectedUser[]>(
    []
  );
  const didApplyDefaultAssignee = useRef(false);
  useEffect(() => {
    if (didApplyDefaultAssignee.current || !localUser) return;
    didApplyDefaultAssignee.current = true;
    setSelectedAssignees([
      { id: localUser.id, name: displayNameForUser(localUser) },
    ]);
  }, [localUser]);
  const [selectedBrokerTeams, setSelectedBrokerTeams] = useState<
    { name: string; code: string }[]
  >([]);
  const [effectiveDateStart, setEffectiveDateStart] = useState("");
  const [effectiveDateEnd, setEffectiveDateEnd] = useState("");

  const brokerTeams = useMemo(() => {
    const teams = new Set<string>();
    for (const row of data ?? []) {
      if (row.google_group_email) {
        teams.add(row.google_group_email);
      }
    }
    return Array.from(teams)
      .sort()
      .map((email) => ({
        name: email.split("@")[0] ?? "",
        code: email,
      }));
  }, [data]);

  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((row) => {
      if (searchText.trim() && !matchesSearch(row, searchText)) {
        return false;
      }
      if (
        selectedStatuses.length > 0 &&
        !selectedStatuses.some((s) => s.code === row.policy_status)
      ) {
        return false;
      }
      if (selectedAssignees.length > 0) {
        const assigneeMatch = selectedAssignees.some((u) => {
          if (u.id === "Unassigned") return !row.assignee;
          return u.id === row.assignee;
        });
        if (!assigneeMatch) return false;
      }
      if (
        selectedBrokerTeams.length > 0 &&
        !selectedBrokerTeams.some((t) => t.code === row.google_group_email)
      ) {
        return false;
      }
      if (effectiveDateStart || effectiveDateEnd) {
        const effDate = mmddyyyyToDate(
          row.report_metadata.company_info.private_computed_values
            .effective_date
        );
        if (!effDate) return false;
        if (effectiveDateStart) {
          const start = mmddyyyyToDate(effectiveDateStart);
          if (start && effDate < start) return false;
        }
        if (effectiveDateEnd) {
          const end = mmddyyyyToDate(effectiveDateEnd);
          if (end && effDate > end) return false;
        }
      }
      return true;
    });
  }, [
    data,
    searchText,
    selectedStatuses,
    selectedAssignees,
    selectedBrokerTeams,
    effectiveDateStart,
    effectiveDateEnd,
  ]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.searchContainer}>
        <InputText
          className={styles.searchInput}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search Policy Fly applications..."
        />
      </div>
      <div className={styles.rowsContainer}>
        <DataTable
          value={filteredData}
          scrollable
          scrollHeight="calc(100vh - 234px)"
          paginator
          rows={30}
          sortField="total_premium"
          sortOrder={-1}
          onRowClick={(e) => {
            const row = e.data as PolicyFlyApplicationRow;
            navigate(ROUTES.DASHBOARD_REPORT(row.report_id, "general"));
          }}
          rowClassName={() => styles.row}
          pt={{
            headerRow: {
              style: { boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.15)" },
            },
          }}
          emptyMessage={
            selectedAssignees.length === 1 &&
            selectedAssignees[0]?.id === localUser?.id
              ? "Nothing assigned to you."
              : "No Policy Fly applications found."
          }
        >
          <Column
            columnKey="spacer"
            key="spacer"
            style={{ width: "32px", padding: "16px 8px" }}
            header=""
            pt={columnPtOptions}
          />
          <Column
            columnKey="pf_status"
            key="pf_status"
            header={() => (
              <div className={styles.horizontalContainer}>
                <span>Status</span>
                <div onClick={(e) => e.stopPropagation()}>
                  <FilterDropdown
                    options={PF_STATUS_OPTIONS}
                    selectedOptions={selectedStatuses}
                    setSelectedOptions={setSelectedStatuses}
                  />
                </div>
              </div>
            )}
            style={{ width: "140px", padding: "16px 8px" }}
            body={(row: PolicyFlyApplicationRow) => (
              <span>{readablePolicyFlyStatus(row.policy_status)}</span>
            )}
            pt={columnPtOptions}
          />
          <Column
            columnKey="assignee"
            key="assignee"
            style={{ width: "160px", padding: "16px 8px" }}
            header={() => (
              <div className={styles.horizontalContainer}>
                <AssigneeFilterDropdown
                  icon={"pi pi-user-edit"}
                  selectedUsers={selectedAssignees}
                  setSelectedUsers={setSelectedAssignees}
                />
                <span>
                  {featureEnabled(clientId, "assignee-named-underwriter")
                    ? "Underwriter"
                    : "Assignee"}
                </span>
              </div>
            )}
            body={(row: PolicyFlyApplicationRow) => {
              const assignee = users.data?.all_company_users.find(
                (u) => u.id === row.assignee
              );
              return (
                <PantheonAvatarWithoutMenu
                  label={displayNameForUser(assignee)}
                  showName={true}
                />
              );
            }}
            pt={columnPtOptions}
          />
          <Column
            columnKey="company_name"
            key="company_name"
            header="Company Name"
            style={{ width: "250px", padding: "16px 8px" }}
            field="report_metadata.company_info.private_computed_values.company_name"
            sortable
            body={(row: PolicyFlyApplicationRow) => (
              <span className={styles.twoLineText}>
                {
                  row.report_metadata.company_info.private_computed_values
                    .company_name
                }
              </span>
            )}
            pt={columnPtOptions}
          />
          {featureEnabled(clientId, "show-states") && (
            <Column
              columnKey="states"
              key="states"
              header="States"
              style={{ width: "112px", padding: "16px 8px" }}
              body={(row: PolicyFlyApplicationRow) => (
                <StateCards
                  states={
                    row.report_metadata.precomputed_metadata
                      .unique_location_states ?? []
                  }
                  reportId={row.report_id}
                />
              )}
              pt={columnPtOptions}
            />
          )}
          <Column
            columnKey="google_group_email"
            key="google_group_email"
            header={() => (
              <div className={styles.horizontalContainer}>
                <span>Broker Team</span>
                <div onClick={(e) => e.stopPropagation()}>
                  <FilterDropdown
                    options={brokerTeams}
                    selectedOptions={selectedBrokerTeams}
                    setSelectedOptions={setSelectedBrokerTeams}
                    itemTemplate={(option) => (
                      <GoogleGroupIcon groupName={option.code} />
                    )}
                  />
                </div>
              </div>
            )}
            style={{ width: "180px", padding: "16px 8px" }}
            body={(row: PolicyFlyApplicationRow) => (
              <GoogleGroupIcon groupName={row.google_group_email} />
            )}
            pt={columnPtOptions}
          />
          {featureEnabled(clientId, "show-brokerage") && (
            <Column
              columnKey="broker"
              key="broker"
              header="Broker"
              style={{ width: "186px", padding: "16px 8px" }}
              body={(row: PolicyFlyApplicationRow) => (
                <CRMContact
                  contact={row.broker_contact}
                  brokerageName={row.broker_contact?.organization_name}
                  reportId={row.report_id}
                />
              )}
              pt={columnPtOptions}
            />
          )}
          <Column
            columnKey="eff_date"
            key="eff_date"
            header={() => (
              <div className={styles.horizontalContainer}>
                <span>Effective Date</span>
                <div onClick={(e) => e.stopPropagation()}>
                  <EffectiveDateFilter
                    effectiveDateStart={effectiveDateStart}
                    effectiveDateEnd={effectiveDateEnd}
                    onChangeStart={setEffectiveDateStart}
                    onChangeEnd={setEffectiveDateEnd}
                    onClearBoth={() => {
                      setEffectiveDateStart("");
                      setEffectiveDateEnd("");
                    }}
                    onChangeBoth={(start, end) => {
                      setEffectiveDateStart(start);
                      setEffectiveDateEnd(end);
                    }}
                  />
                </div>
              </div>
            )}
            style={{ width: "160px", padding: "16px 8px" }}
            field="report_metadata.company_info.private_computed_values.effective_date"
            sortable
            body={(row: PolicyFlyApplicationRow) => {
              const isAsap =
                row.report_metadata.company_info.private_computed_values
                  .effective_date === "ASAP";
              return (
                <div className={styles.horizontalContainer}>
                  <span>
                    {
                      row.report_metadata.company_info.private_computed_values
                        .effective_date
                    }
                  </span>
                  {isAsap && <i className={`pi pi-clock ${styles.asapIcon}`} />}
                </div>
              );
            }}
            pt={columnPtOptions}
          />
          <Column
            columnKey="tiv"
            key="tiv"
            header="TIV"
            style={{ width: "112px", padding: "16px 8px" }}
            field="report_metadata.precomputed_metadata.total_tiv"
            sortable
            body={(row: PolicyFlyApplicationRow) => (
              <span className={styles.monoMoney}>
                {formatAbbreviatedMoney(
                  row.report_metadata.precomputed_metadata.total_tiv
                )}
              </span>
            )}
            pt={columnPtOptions}
          />
          <Column
            columnKey="premium"
            key="premium"
            header="Premium"
            style={{ width: "120px", padding: "16px 8px" }}
            field="total_premium"
            sortable
            body={(row: PolicyFlyApplicationRow) => {
              const prem = row.total_premium;
              return (
                <div className={styles.severityMoney}>
                  <div
                    className={styles.severityBar}
                    style={{
                      backgroundColor: borderColorForAttributes(
                        premiumSeverity(prem),
                        isLightMode
                      ),
                    }}
                  />
                  <span className={styles.monoMoney}>
                    {formatAbbreviatedMoney(prem)}
                  </span>
                </div>
              );
            }}
            pt={columnPtOptions}
          />
          <Column
            columnKey="layer"
            key="layer"
            header="Layer"
            style={{ width: "180px", padding: "16px 8px" }}
            body={(row: PolicyFlyApplicationRow) => (
              <span className={styles.twoLineText}>
                {row.layer
                  ? readableLayer({
                      layer_id: "",
                      starting_point: row.layer.starting_point,
                      range: row.layer.range,
                      target: null,
                      rated: null,
                      participation: row.layer.participation,
                    })
                  : null}
              </span>
            )}
            pt={columnPtOptions}
          />
          <Column
            columnKey="share"
            key="share"
            header="Share %"
            style={{ width: "100px", padding: "16px 8px" }}
            sortable
            sortField="layer.participation"
            body={(row: PolicyFlyApplicationRow) => {
              if (!row.layer || row.layer.range <= 0) return null;
              const participation = row.layer.participation ?? row.layer.range;
              const pct = (participation / row.layer.range) * 100;
              const rounded = Math.round(pct * 10) / 10;
              return (
                <span className={styles.monoMoney}>
                  {Number.isInteger(rounded) ? rounded.toFixed(0) : rounded}%
                </span>
              );
            }}
            pt={columnPtOptions}
          />
          <Column
            columnKey="pf_link"
            key="pf_link"
            header=""
            style={{ width: "60px", padding: "16px 8px" }}
            body={(row: PolicyFlyApplicationRow) => {
              const applicationUrl = row.application_url;
              if (!applicationUrl) return null;
              return (
                <Button
                  icon="pi pi-external-link"
                  rounded
                  text
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(applicationUrl, "_blank");
                  }}
                  tooltip="Open in Policy Fly"
                  tooltipOptions={{ showDelay: 500 }}
                />
              );
            }}
            pt={columnPtOptions}
          />
          <Column
            columnKey="spacer_end"
            key="spacer_end"
            style={{ width: "32px", padding: "16px 8px" }}
            header=""
            pt={columnPtOptions}
          />
        </DataTable>
      </div>
    </div>
  );
};
